import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  RiAddLine,
  RiSearchLine,
  RiCloseLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiFileTextLine,
  RiCalendarLine,
  RiMoneyDollarCircleLine,
  RiStore2Line,
  RiPriceTag3Line,
  RiBankLine,
  RiBuilding2Line,
  RiListCheck2,
  RiWalletLine,
  RiCoinLine,
  RiTimeLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/branding-order`;
const CATEGORY_URL = `${process.env.REACT_APP_API_URL}/vendor-category`;
const VENDOR_URL = `${process.env.REACT_APP_API_URL}/branding-vendor`;
const AGENCY_URL = `${process.env.REACT_APP_API_URL}/referral-agency`;
const BANK_URL = `${process.env.REACT_APP_API_URL}/bank`;

const defaultForm = {
  client: "",
  category: "",
  vendor: "",
  referralAgency: "",
  billNo: "",
  billDate: "",
  amount: "",
  paidAmount: "",
  paymentMethod: "other",
};

const defaultPaymentForm = {
  paidAmount: "",
  paymentMethod: "other",
};

/* ─── Helpers ─── */

const Spinner = () => (
  <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const Modal = ({ title, onClose, children, size = "lg" }) =>
  createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative z-10 w-full ${
          size === "lg" ? "max-w-3xl" : "max-w-lg"
        } mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

/* ─── Status Badge ─── */
const StatusBadge = ({ status }) => {
  const map = {
    paid: "bg-emerald-50 border-emerald-200 text-emerald-700",
    partial: "bg-amber-50 border-amber-200 text-amber-700",
    pending: "bg-red-50 border-red-200 text-red-600",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold capitalize ${
        map[status] || map.pending
      }`}
    >
      {status}
    </span>
  );
};

/* ─── SelectField ─── */
const SelectField = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder,
  Icon,
  required,
}) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
      )}
      <select
        name={name}
        value={value}
        onChange={onChange}
        className={`w-full bg-blue-50/60 border border-blue-200 rounded-lg ${
          Icon ? "pl-9" : "pl-3"
        } pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none`}
      >
        <option value="">{placeholder || `Select ${label}…`}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  </div>
);

/* ─── InputField ─── */
const InputField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  Icon,
  required,
}) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder || label}
        className={`w-full bg-blue-50/60 border border-blue-200 rounded-lg ${
          Icon ? "pl-9" : "pl-3"
        } pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition`}
      />
    </div>
  </div>
);

const paymentMethodOptions = [
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "upi", label: "UPI" },
  { value: "other", label: "Other" },
];

/* ─── Form Body ─── */
const FormBody = ({
  form,
  handleChange,
  clients,
  categories,
  vendors,
  agencies,
}) => (
  <div className="grid grid-cols-2 gap-4 mb-6">
    {/* Client */}
    <div className="col-span-2">
      <SelectField
        label="Client (Bank)"
        name="client"
        value={form.client}
        onChange={handleChange}
        options={clients.map((c) => ({ value: c._id, label: c.bankName }))}
        Icon={RiBankLine}
        required
      />
    </div>

    {/* Category */}
    <SelectField
      label="Category"
      name="category"
      value={form.category}
      onChange={handleChange}
      options={categories.map((c) => ({ value: c._id, label: c.name }))}
      Icon={RiPriceTag3Line}
      required
    />

    {/* Vendor */}
    <SelectField
      label="Vendor"
      name="vendor"
      value={form.vendor}
      onChange={handleChange}
      options={vendors.map((v) => ({ value: v._id, label: v.name }))}
      Icon={RiStore2Line}
      required
    />

    {/* Referral Agency */}
    <div className="col-span-2">
      <SelectField
        label="Referral Agency (Optional)"
        name="referralAgency"
        value={form.referralAgency}
        onChange={handleChange}
        options={agencies.map((a) => ({ value: a._id, label: a.agencyName }))}
        Icon={RiBuilding2Line}
        placeholder="None"
      />
    </div>

    {/* Bill No */}
    <InputField
      label="Bill No"
      name="billNo"
      value={form.billNo}
      onChange={handleChange}
      Icon={RiFileTextLine}
      placeholder="e.g. BILL-001"
      required
    />

    {/* Bill Date */}
    <InputField
      label="Bill Date"
      name="billDate"
      value={form.billDate}
      onChange={handleChange}
      type="date"
      Icon={RiCalendarLine}
      required
    />

    {/* Amount */}
    <InputField
      label="Total Amount (₹)"
      name="amount"
      value={form.amount}
      onChange={handleChange}
      type="number"
      Icon={RiMoneyDollarCircleLine}
      placeholder="0"
      required
    />

    {/* Paid Amount */}
    <InputField
      label="Paid Amount (₹)"
      name="paidAmount"
      value={form.paidAmount}
      onChange={handleChange}
      type="number"
      Icon={RiCoinLine}
      placeholder="0"
    />

    {/* Payment Method */}
    <div className="col-span-2">
      <SelectField
        label="Payment Method"
        name="paymentMethod"
        value={form.paymentMethod}
        onChange={handleChange}
        options={paymentMethodOptions}
        Icon={RiWalletLine}
      />
    </div>
  </div>
);

/* ─── Payment Update Form ─── */
const PaymentFormBody = ({ form, handleChange }) => (
  <div className="flex flex-col gap-4 mb-6">
    <InputField
      label="Paid Amount (₹)"
      name="paidAmount"
      value={form.paidAmount}
      onChange={handleChange}
      type="number"
      Icon={RiCoinLine}
      placeholder="0"
      required
    />
    <SelectField
      label="Payment Method"
      name="paymentMethod"
      value={form.paymentMethod}
      onChange={handleChange}
      options={paymentMethodOptions}
      Icon={RiWalletLine}
    />
  </div>
);

/* ─── Main Component ─── */
export default function BrandingOrders() {
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null); // create | edit | delete | payment
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [paymentForm, setPaymentForm] = useState(defaultPaymentForm);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });

  const notify = (message, type = "success") => setToast({ message, type });

  const fetchLookups = async () => {
    const [catRes, vendRes, agencyRes, bankRes] = await Promise.allSettled([
      axios.get(`${CATEGORY_URL}/`),
      axios.get(`${VENDOR_URL}/`),
      axios.get(`${AGENCY_URL}/all`),
      axios.get(`${BANK_URL}/get-banks`),
    ]);

    if (catRes.status === "fulfilled")
      setCategories(catRes.value.data?.data || catRes.value.data || []);
    else notify("Failed to fetch categories", "error");

    if (vendRes.status === "fulfilled")
      setVendors(vendRes.value.data?.data || vendRes.value.data || []);
    else notify("Failed to fetch vendors", "error");

    if (agencyRes.status === "fulfilled")
      setAgencies(agencyRes.value.data?.data || agencyRes.value.data || []);
    else notify("Failed to fetch agencies", "error");

    if (bankRes.status === "fulfilled")
      setClients(bankRes.value.data?.data || bankRes.value.data || []);
    else notify("Failed to fetch clients", "error");
  };

  const fetchOrders = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (filterStatus) params.paymentStatus = filterStatus;
      const res = await axios.get(`${BASE_URL}/`, { params });
      setOrders(res.data?.data || []);
      if (res.data?.pagination) setPagination(res.data.pagination);
    } catch {
      notify("Failed to fetch orders", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLookups();
  }, []);
  useEffect(() => {
    fetchOrders(1);
  }, [filterStatus]);

  /* ─── Modal helpers ─── */
  const openCreate = () => {
    setForm(defaultForm);
    setModal("create");
  };
  const openEdit = (order) => {
    setSelected(order);
    setForm({
      client: order.client?._id || order.client || "",
      category: order.category?._id || order.category || "",
      vendor: order.vendor?._id || order.vendor || "",
      referralAgency: order.referralAgency?._id || order.referralAgency || "",
      billNo: order.billNo || "",
      billDate: order.billDate ? order.billDate.slice(0, 10) : "",
      amount: order.amount ?? "",
      paidAmount: order.paidAmount ?? "",
      paymentMethod: order.paymentMethod || "other",
    });
    setModal("edit");
  };
  const openDelete = (order) => {
    setSelected(order);
    setModal("delete");
  };
  const openPayment = (order) => {
    setSelected(order);
    setPaymentForm({
      paidAmount: order.paidAmount ?? "",
      paymentMethod: order.paymentMethod || "other",
    });
    setModal("payment");
  };
  const closeModal = () => {
    setModal(null);
    setSelected(null);
  };

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const handlePaymentChange = (e) =>
    setPaymentForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleCreate = async () => {
    if (
      !form.client ||
      !form.category ||
      !form.vendor ||
      !form.billNo ||
      !form.amount
    ) {
      notify(
        "Client, category, vendor, bill no, and amount are required",
        "error",
      );
      return;
    }
    setActionLoading(true);
    try {
      const payload = { ...form };
      if (!payload.referralAgency) delete payload.referralAgency; // ← fix
      await axios.post(`${BASE_URL}/`, payload);
      notify("Order created successfully");
      fetchOrders();
      closeModal();
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to create order", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (
      !form.client ||
      !form.category ||
      !form.vendor ||
      !form.billNo ||
      !form.amount
    ) {
      notify(
        "Client, category, vendor, bill no, and amount are required",
        "error",
      );
      return;
    }
    setActionLoading(true);
    try {
      const payload = { ...form };
      if (!payload.referralAgency) delete payload.referralAgency; // ← fix
      await axios.put(`${BASE_URL}/${selected._id}`, payload);
      notify("Order updated successfully");
      fetchOrders();
      closeModal();
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to update order", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/${selected._id}`);
      notify("Order deleted successfully");
      fetchOrders();
      closeModal();
    } catch {
      notify("Failed to delete order", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePaymentUpdate = async () => {
    if (paymentForm.paidAmount === "") {
      notify("Paid amount is required", "error");
      return;
    }
    setActionLoading(true);
    try {
      await axios.patch(`${BASE_URL}/${selected._id}/payment`, paymentForm);
      notify("Payment updated successfully");
      fetchOrders();
      closeModal();
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to update payment",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  /* ─── Client-side search filter ─── */
  const filtered = orders.filter(
    (o) =>
      (o.billNo || "").toLowerCase().includes(search.toLowerCase()) ||
      (o.client?.bankName || "").toLowerCase().includes(search.toLowerCase()) ||
      (o.vendor?.name || "").toLowerCase().includes(search.toLowerCase()),
  );

  const fmt = (n) =>
    typeof n === "number"
      ? n.toLocaleString("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 0,
        })
      : "—";

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  const tableHeaders = [
    { label: "#" },
    { label: "Bill No", Icon: RiFileTextLine },
    { label: "Client", Icon: RiBankLine },
    { label: "Category", Icon: RiPriceTag3Line },
    { label: "Vendor", Icon: RiStore2Line },
    { label: "Bill Date", Icon: RiCalendarLine },
    { label: "Amount", Icon: RiMoneyDollarCircleLine },
    { label: "Paid", Icon: RiCoinLine },
    { label: "Due", Icon: RiTimeLine },
    { label: "Status" },
    { label: "Actions" },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-800">
      {/* ── Header ── */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiFileTextLine className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Branding Orders
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Order Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Status filter */}
            <div className="relative">
              <RiListCheck2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400 pointer-events-none" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-40 transition appearance-none"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
              </select>
            </div>

            {/* Search */}
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search orders..."
                className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-52 transition"
              />
            </div>

            {/* Refresh */}
            <button
              onClick={() => fetchOrders()}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>

            <button
              onClick={openCreate}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
            >
              <RiAddLine className="w-4 h-4" />
              Add Order
            </button>
          </div>
        </div>
      </header>

      {/* ── Table ── */}
      <div className="w-full mx-auto px-6 pt-6 pb-10">
        <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <RiListCheck2 className="w-4 h-4 text-blue-500" />
              All Branding Orders
            </span>
            <span className="text-xs text-blue-400">
              {pagination.totalItems} record
              {pagination.totalItems !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/30">
                  {tableHeaders.map(({ label, Icon }) => (
                    <th key={label} className="px-4 py-3 text-left">
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                        {label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={11} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-400">
                        <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-blue-500" />
                        <span className="text-sm">Loading orders…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiFileTextLine className="w-7 h-7 text-blue-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          {search || filterStatus
                            ? "No orders match your filters."
                            : "No orders found. Add one to get started."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((order, i) => (
                    <tr
                      key={order._id || i}
                      className="border-b border-blue-50 hover:bg-blue-50/50 transition group"
                    >
                      <td className="px-4 py-3 text-slate-400 text-xs font-medium">
                        {(pagination.currentPage - 1) * 10 + i + 1}
                      </td>
                      <td className="px-4 py-3 font-semibold text-blue-700 whitespace-nowrap">
                        {order.billNo || "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-bold shadow-sm shadow-blue-200 flex-shrink-0">
                            {(order.client?.bankName || "?")[0].toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-800">
                            {order.client?.bankName || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-600 text-xs font-semibold whitespace-nowrap">
                          <RiPriceTag3Line className="w-3 h-3" />
                          {order.category?.name || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {order.vendor?.name || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                        {fmtDate(order.billDate)}
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                        {fmt(order.amount)}
                      </td>
                      <td className="px-4 py-3 text-emerald-600 font-medium whitespace-nowrap">
                        {fmt(order.paidAmount)}
                      </td>
                      <td className="px-4 py-3 text-red-500 font-medium whitespace-nowrap">
                        {fmt(order.dueAmount)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={order.paymentStatus} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5 flex-nowrap">
                          <button
                            onClick={() => openPayment(order)}
                            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-100 font-semibold transition whitespace-nowrap"
                            title="Update Payment"
                          >
                            <RiWalletLine className="w-3.5 h-3.5" /> Pay
                          </button>
                          <button
                            onClick={() => openEdit(order)}
                            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
                          >
                            <RiEditLine className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            onClick={() => openDelete(order)}
                            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                          >
                            <RiDeleteBinLine className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-5 py-3 border-t border-blue-50 bg-blue-50/30 flex items-center justify-between">
              <span className="text-xs text-blue-400">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  disabled={pagination.currentPage <= 1}
                  onClick={() => fetchOrders(pagination.currentPage - 1)}
                  className="px-3 py-1.5 rounded-lg text-xs border border-blue-200 text-blue-600 hover:bg-blue-100 disabled:opacity-40 transition"
                >
                  Prev
                </button>
                <button
                  disabled={pagination.currentPage >= pagination.totalPages}
                  onClick={() => fetchOrders(pagination.currentPage + 1)}
                  className="px-3 py-1.5 rounded-lg text-xs border border-blue-200 text-blue-600 hover:bg-blue-100 disabled:opacity-40 transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Create Modal ── */}
      {modal === "create" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add New Order
            </>
          }
          onClose={closeModal}
        >
          <FormBody
            form={form}
            handleChange={handleChange}
            clients={clients}
            categories={categories}
            vendors={vendors}
            agencies={agencies}
          />
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Create Order
            </button>
          </div>
        </Modal>
      )}

      {/* ── Edit Modal ── */}
      {modal === "edit" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit —{" "}
              {selected?.billNo}
            </>
          }
          onClose={closeModal}
        >
          <FormBody
            form={form}
            handleChange={handleChange}
            clients={clients}
            categories={categories}
            vendors={vendors}
            agencies={agencies}
          />
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {/* ── Payment Update Modal ── */}
      {modal === "payment" && (
        <Modal
          title={
            <>
              <RiWalletLine className="w-5 h-5 text-emerald-500" /> Update
              Payment — {selected?.billNo}
            </>
          }
          onClose={closeModal}
          size="sm"
        >
          <div className="mb-4 p-3 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-between text-sm">
            <span className="text-blue-600 font-medium">Total Amount</span>
            <span className="font-bold text-slate-800">
              {fmt(selected?.amount)}
            </span>
          </div>
          <PaymentFormBody
            form={paymentForm}
            handleChange={handlePaymentChange}
          />
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handlePaymentUpdate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition shadow-md shadow-emerald-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Update Payment
            </button>
          </div>
        </Modal>
      )}

      {modal === "delete" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
          size="sm"
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete order{" "}
              <span className="text-red-500">{selected?.billNo}</span>?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This action cannot be undone. This order will be permanently
              removed.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Delete Order
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
